import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Eye, 
  Users, 
  Stethoscope, 
  Search, 
  ExternalLink,
  UserCircle,
  Calendar,
  BookOpen,
  Trophy,
  MessageSquare
} from "lucide-react";

export function AdminImpersonation() {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"clinician" | "client">("clinician");

  // Fetch all clinicians
  const { data: clinicians, isLoading: cliniciansLoading } = useQuery({
    queryKey: ['admin-clinicians'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clinician_profiles')
        .select(`
          id,
          user_id,
          practice_name,
          practitioner_type,
          professional_title,
          created_at
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  // Fetch all clients (users who have clinician connections)
  const { data: clients, isLoading: clientsLoading } = useQuery({
    queryKey: ['admin-clients'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clinician_clients')
        .select(`
          id,
          client_id,
          clinician_id,
          status,
          created_at,
          client_profile:profiles!clinician_clients_client_id_fkey(
            display_name,
            avatar_url
          )
        `)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Deduplicate by client_id
      const uniqueClients = data?.reduce((acc, curr) => {
        if (!acc.find(c => c.client_id === curr.client_id)) {
          acc.push(curr);
        }
        return acc;
      }, [] as typeof data) || [];
      
      return uniqueClients;
    }
  });

  const filteredClinicians = clinicians?.filter(c => 
    c.practice_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.professional_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredClients = clients?.filter(c => {
    const name = (c.client_profile as any)?.display_name || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const openClinicianView = (clinicianId: string) => {
    // Open clinician portal in new tab with impersonation param
    window.open(`/clinician?view_as=${clinicianId}`, '_blank');
  };

  const openClientView = (clientId: string, clinicianId: string) => {
    // Open client portal in new tab with impersonation param
    window.open(`/client-portal?view_as=${clientId}&clinician=${clinicianId}`, '_blank');
  };

  return (
    <div className="space-y-6">
      <Card className="bg-card/50 border-border/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            View As User
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Preview the app as any clinician or client to test functionality and troubleshoot issues.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as any)}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="clinician" className="gap-2">
                <Stethoscope className="h-4 w-4" />
                Clinicians ({clinicians?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="client" className="gap-2">
                <Users className="h-4 w-4" />
                Clients ({clients?.length || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="clinician">
              {cliniciansLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
                </div>
              ) : filteredClinicians?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No clinicians found
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredClinicians?.map(clinician => (
                    <Card key={clinician.id} className="bg-muted/30 border-border/30 hover:bg-muted/50 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Stethoscope className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <h4 className="font-medium text-foreground">
                                {clinician.practice_name || 'Unnamed Practice'}
                              </h4>
                              <p className="text-xs text-muted-foreground">
                                {clinician.professional_title || clinician.practitioner_type}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="text-xs capitalize">
                            {clinician.practitioner_type || 'therapist'}
                          </Badge>
                          <Button
                            size="sm"
                            onClick={() => openClinicianView(clinician.user_id)}
                            className="gap-1"
                          >
                            <Eye className="h-3 w-3" />
                            View Portal
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="client">
              {clientsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
                </div>
              ) : filteredClients?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No clients found
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredClients?.map(client => (
                    <Card key={client.id} className="bg-muted/30 border-border/30 hover:bg-muted/50 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-secondary/30 flex items-center justify-center">
                              <UserCircle className="h-5 w-5 text-secondary-foreground" />
                            </div>
                            <div>
                              <h4 className="font-medium text-foreground">
                                {(client.client_profile as any)?.display_name || 'Anonymous Client'}
                              </h4>
                              <p className="text-xs text-muted-foreground">
                                Client ID: {client.client_id.slice(0, 8)}...
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <Badge 
                            variant="secondary" 
                            className={`text-xs ${client.status === 'active' ? 'bg-green-500/10 text-green-500' : ''}`}
                          >
                            {client.status}
                          </Badge>
                          <Button
                            size="sm"
                            onClick={() => openClientView(client.client_id, client.clinician_id)}
                            className="gap-1"
                          >
                            <Eye className="h-3 w-3" />
                            View Portal
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="bg-card/50 border-border/30">
        <CardHeader>
          <CardTitle className="text-lg">Quick Preview Links</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Button
              variant="outline"
              className="justify-start gap-2 h-auto py-3"
              onClick={() => window.open('/clinician', '_blank')}
            >
              <Stethoscope className="h-4 w-4 text-primary" />
              <div className="text-left">
                <div className="font-medium">Clinician Portal</div>
                <div className="text-xs text-muted-foreground">Full clinician view</div>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="justify-start gap-2 h-auto py-3"
              onClick={() => window.open('/client-portal', '_blank')}
            >
              <Users className="h-4 w-4 text-secondary-foreground" />
              <div className="text-left">
                <div className="font-medium">Client Portal</div>
                <div className="text-xs text-muted-foreground">Full client view</div>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="justify-start gap-2 h-auto py-3"
              onClick={() => window.open('/dashboard', '_blank')}
            >
              <Calendar className="h-4 w-4 text-blue-500" />
              <div className="text-left">
                <div className="font-medium">Main Dashboard</div>
                <div className="text-xs text-muted-foreground">User dashboard</div>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="justify-start gap-2 h-auto py-3"
              onClick={() => window.open('/', '_blank')}
            >
              <BookOpen className="h-4 w-4 text-green-500" />
              <div className="text-left">
                <div className="font-medium">Landing Page</div>
                <div className="text-xs text-muted-foreground">Public homepage</div>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
